import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, ArrowLeft, AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useBrand } from "@/contexts/BrandContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_PW_LENGTH = 15;

function PasswordStrength({ password }) {
  const score = [password.length >= MIN_PW_LENGTH, /[A-Z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length;
  const labels = ["", "Too short", "Fair", "Good", "Strong"];
  const colors = ["", "bg-rose-400", "bg-amber-400", "bg-lime-400", "bg-emerald-500"];
  if (!password) return null;
  return (
    <div className="mt-1.5">
      <div className="flex gap-1 mb-1">
        {[1,2,3,4].map(i => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= score ? colors[score] : "bg-[#E5E5E5]"}`} />
        ))}
      </div>
      <p className={`text-xs ${score >= 3 ? "text-emerald-600" : "text-[#706E6B]"}`}>{labels[score] || ""}</p>
    </div>
  );
}

export default function ResetPassword() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword]     = useState("");
  const [confirm, setConfirm]       = useState("");
  const [showPwd, setShowPwd]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [success, setSuccess]       = useState(false);
  const [error, setError]           = useState("");

  useEffect(() => {
    if (!token) setError("Reset link is missing or invalid. Please request a new one.");
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < MIN_PW_LENGTH) { setError(`Password must be at least ${MIN_PW_LENGTH} characters.`); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (res.status === 429) { setError("Too many attempts. Please wait and try again."); return; }
      if (!res.ok) { setError(data.detail || "Reset failed. The link may have expired."); return; }
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3500);
    } catch {
      setError("Unable to reach the server. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F3F3F3] px-4">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded bg-[#0176D3] flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-white" />
          </div>
          <h1 data-testid="brand-product-name" className="text-3xl font-bold text-[#181818] font-sans tracking-tight">
            {brand?.productName}
          </h1>
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-lg p-8" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
          {!success ? (
            <>
              <h2 className="text-lg font-semibold text-[#181818] mb-1">Set a new password</h2>
              <p className="text-sm text-[#706E6B] mb-6">Choose a strong password for your account.</p>

              <form data-testid="reset-password-form" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#444] mb-1.5">New password</label>
                  <div className="relative">
                    <input
                      data-testid="reset-password-new-input"
                      type={showPwd ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      placeholder="At least 15 characters"
                      className="w-full px-3 py-2.5 pr-10 border border-[#D9D9D9] rounded-lg text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#706E6B] hover:text-[#181818]"
                    >
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <PasswordStrength password={password} />
                  <p className="text-xs text-[#706E6B] mt-1">Min {MIN_PW_LENGTH} characters · Passphrases allowed</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-[#444] mb-1.5">Confirm password</label>
                  <input
                    data-testid="reset-password-confirm-input"
                    type={showPwd ? "text" : "password"}
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    required
                    placeholder="Repeat your new password"
                    className="w-full px-3 py-2.5 border border-[#D9D9D9] rounded-lg text-sm focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] bg-white"
                  />
                </div>

                {error && (
                  <div data-testid="reset-password-error" className="flex items-center gap-2 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="reset-password-submit-btn"
                  type="submit"
                  disabled={loading || !token || !password || !confirm}
                  className="w-full py-2.5 px-4 rounded-lg text-sm font-medium bg-[#0176D3] text-white hover:bg-[#014486] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Saving…" : "Set new password"}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-password-success" className="text-center py-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-base font-semibold text-[#181818] mb-2">Password updated</h3>
              <p className="text-sm text-[#706E6B]">Your password has been changed. All existing sessions have been signed out. Redirecting you to sign in…</p>
            </div>
          )}
        </div>

        {!success && (
          <div className="mt-6 text-center">
            <Link
              to="/login"
              data-testid="reset-password-back-to-login"
              className="inline-flex items-center gap-1.5 text-sm text-[#0176D3] hover:text-[#014486] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
